import { clean } from './product';

/**
 * The last resort, for a page with no structured data of any kind: whatever
 * `<title>` says, and the meta description if there is one.
 *
 * Images don't come from here — `images.ts` scans `<img>` tags whatever the
 * text came from, so this stays about words.
 */

export type PageText = {
  title: string | undefined;
  /**
   * `<title>` before the site name was stripped off it. Shops set their meta
   * description to one spelling or the other, and a description that only
   * restates either is not a description.
   */
  rawTitle: string | undefined;
  description: string | undefined;
};

export function pageText(doc: Document, finalUrl: string): PageText {
  const rawTitle = clean(doc.querySelector('title')?.textContent);

  return {
    title: withoutSiteName(rawTitle, finalUrl),
    rawTitle,
    description: clean(doc.querySelector('meta[name="description" i]')?.getAttribute('content')),
  };
}

/**
 * "Andes Sofa | West Elm" → "Andes Sofa".
 *
 * Only when the trailing segment really is the site's own name, checked
 * against the hostname. The tempting general rule — drop everything after the
 * last separator — eats half of "Sofa, 3-Seater, Charcoal".
 */
function withoutSiteName(title: string | undefined, finalUrl: string): string | undefined {
  if (title === undefined) return undefined;

  let host: string;
  try {
    host = new URL(finalUrl).hostname;
  } catch {
    return title;
  }

  // "www.westelm.co.uk" → "westelm"
  const brand = squash(host.replace(/^www\./, '').split('.')[0] ?? '');
  if (brand === '') return title;

  const segments = title.split(/\s+[|·—–-]\s+/);
  if (segments.length < 2) return title;

  const last = segments[segments.length - 1];
  if (last === undefined || squash(last) !== brand) return title;

  const kept = segments.slice(0, -1).join(' - ').trim();
  return kept === '' ? title : kept;
}

function squash(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}
